import {
  Component,
  OnInit,
  Injector,
  Input,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Platform } from "@ionic/angular";
import moment from "moment";
import { environment } from "src/environments/environment";
import { GenericBasicGUI } from "src/model/class/component classes/generic-basic-gui";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { ClickActionType, HAlign } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";

@Component({
    selector: "app-command-button",
    templateUrl: "./command-button.component.html",
    styleUrls: ["./command-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

/**
 * Giorgia - Each supervision component is an html component which extends the corresponding Model, which (at the end)
 * extends the GenericBasicGUI that has the SupervisionComponentAPI (the object taken from the API)
 * Each level of the parenthood work on the specific informations that are associated to it.
 * We need to clean the class because someone are useless, but I still don't know which one.
 */
export class CommandButtonComponent extends GenericBasicGUI implements OnInit {
  // protected text: string;
  _logContext: string = "Command-button.component";
  private timeoutLongPress: ReturnType<typeof setTimeout> = null;
  private isRetriggering: boolean = false;
  private isLongPressStarted: boolean = false;
  private hasSendedClickCommand: boolean = false;

  public cssButtonClass: string = "";
  public stylesChild = {};
  public styles = {
    "font-family": "Arial",
    // tslint:disable-next-line: object-literal-key-quotes
    color: "#000000",
    "font-size": "15px",
    "font-style": "normal", // italic
    "font-weight": "normal", // bol
    "font-decorator": "none", // underline
    "background-image": "",
    overflow: "hidden",
  };

  /**
   * Giorgia -
   * @param injector The injector param is used by the parent classes to inject the services that they needed:
   * using this trick if the parent classes needed some specific service I don't need to inject them here
   * but I can do the injection in the specific class.
   */
  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    [this.styles, this.stylesChild] = super.HandleProperties(
      this.styles,
      this.stylesChild
    );

    if (!this.ShowingInDesktopVersion) {
      /** Reassign hAlignment value if not passed as component parameter */
      if (!this.hAlignment) {
        this.hAlignment = HAlign.CENTER;
      }

      this.styles["margin"] = this.assignComponentMarginByAlignment(
        this.hAlignment.toString()
      );

      /** In list version force user to show button sized 48px x 48px */ ///
      this.cssButtonClass = "mobile-version";
    }
  }

  ngOnDestroy() {
    this.onDestroyChildren();
  }

  /**
   * Giorgia - Recursive function used to build the style object to assign to the component:
   * Each parent class has a getStyles function that keep the styles object, change it if necessary and return the object
   * The build of the style object is distribuited in the parent class to make each class responsible of it's informations
   * (Why? If we have to change a property we can change it in a single place)
   */
  getStyles() { }

  protected setCurrentImage(urlImage: string, stretch: boolean): void {
    if (!this.ShowingInDesktopVersion) {
      this.styles["width"] = "48px";
      this.styles["height"] = "48px";
      this.styles["background-size"] = "cover";
    }

    if (urlImage !== "") {
      this.styles["background-image"] = "url(" + urlImage + ")";

      if (stretch) {
        this.styles["background-size"] = "contain";
      } else {
        this.styles["background-size"] = "initial";
      }
      this.styles["background-position"] = "center";
      this.styles["background-repeat"] = "no-repeat";
    } else {
      this.styles["background"] =
        AppSettings.CUSTOM_INTERFACE_BUTTON_DEFAULT_BACKGROUND;
      this.styles["border"] =
        AppSettings.CUSTOM_INTERFACE_BUTTON_DEFAULT_BORDER;
    }
  }

  /**
   * Mouse down event handler from desktop/web
   * If click action is different to "No action" set pressed image
   * if there is a longPressAction start its timer.
   * 
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED.
   */
  public MouseDown(): void {
    // otherwise on mobile device the command is send 2 times

    if (!this.platform.is("ios") && !this.platform.is("android") && !this.isContainingListScrolling) {
      this.handleStartPressedAction();
    }
  }

  /**
   * GF: handle the touch from device
   * if the button is keep pressed (not just a rapid touch), the event MouseUp and Click is not sended.
   * if there is a longPressAction start its timer.
   * 
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED.
   */
  public TouchStart(): void {
    if (!this.isContainingListScrolling) {
      this.handleStartPressedAction();
    }
  }

  /**
   * Mouse up event handler on desktop/web
   * If click action is different to "No action" set pressed image.
   * 
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED. ALSO, the associated image is un-pressed.
   */
  public MouseUp(): void {
    if (!this.platform.is("ios") && !this.platform.is("android")) {
      if (!this.isContainingListScrolling) {
        this.handleEndPressedAction();
      } else {
        this.SetPressedImage(false);
        this.cssCustomComponetClass = "";
      }
    }
  }

  /**
   * Mouse up event handler on device
   * If click action is different to "No action" set pressed image.
   * 
   * IF the "ion-content" element in which the button is contained is currently scrolling, NO ACTION IS EXECUTED. ALSO, the associated image is un-pressed.
   */
  public TouchEnd(): void {
    if (!this.isContainingListScrolling) {
      this.handleEndPressedAction();
    } else {
      this.cssCustomComponetClass = "";

      this.SetPressedImage(false);
    }
  }

  /**
   * Handle start pressed action
   */
  private handleStartPressedAction(): void {

    this.loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleStartPressedAction()",
      "START"
    );

    if (!this.readOnly) {
      this.hasSendedClickCommand = false;

      if (
        this.ComponentAPI.properties["ClickAction"] !==
        ClickActionType.NO_ACTIONS
      ) {
        this.cssCustomComponetClass = "clicked-supervision-button-element";

        this.SetPressedImage(true);
      }

      this.isLongPressStarted = false;

      this.handleLongPressAction();
    } else {
      this.handleShowReadonlyCommandAlert();
    }
  }

  /**
   * Handle log press action routine
   */
  private handleLongPressAction(): void {
    if (
      this.ComponentAPI.properties["LongPressAction"] &&
      this.ComponentAPI.properties["LongPressAction"] !==
      ClickActionType.NO_ACTIONS
    ) {
      this.cssCustomComponetClass = "clicked-supervision-button-element";

      this.SetPressedImage(true);

      this.isRetriggering = false;
      this.timeoutLongPress = setTimeout(() => {
        this.LongPressOrRetrigger();
      }, this.ComponentAPI.properties["LongPressTiming"]);
    }
  }

  /**
   * Handle end presseed action
   */
  private handleEndPressedAction(): void {
    this.loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleEndPressedAction()",
      "START"
    );

    if (!this.readOnly) {
      this.cssCustomComponetClass = "";

      this.SetPressedImage(false);
      this.LongPressRelease();
    }
  }

  /**
   * Long press action and its retriggering
   */
  private LongPressOrRetrigger(): void {
    if (!this.readOnlyCommand2) {
      this.isLongPressStarted = true;
      if (this.isRetriggering) {
        this.SendCommand(
          this.ComponentAPI.properties["LongPressAction"],
          2,
          false,
          true
        );
      } else {
        this.SendCommand(
          this.ComponentAPI.properties["LongPressAction"],
          2,
          true
        );
      }

      if (
        this.ComponentAPI.properties["LongPressRetriggering"] &&
        this.ComponentAPI.properties["LongPressRetriggering"]
          .toString()
          .toUpperCase() == "TRUE"
      ) {
        this.isRetriggering = true;
        this.timeoutLongPress = setTimeout(() => {
          this.LongPressOrRetrigger();
        }, this.ComponentAPI.properties["LongPressRetriggeringTime"]);
      }
    } else {
      clearTimeout(this.timeoutLongPress);
      this.cssCustomComponetClass = "";
      this.SetPressedImage(false);

      this.handleShowReadonlyCommandAlert();
    }
  }

  /**
   * Release of long press
   */
  private async LongPressRelease(): Promise<void> {
    if (this.timeoutLongPress) {
      clearTimeout(this.timeoutLongPress);
    }

    if (this.isLongPressStarted) {
      this.isLongPressStarted = false;
      this.SendCommand(
        this.ComponentAPI.properties["LongPressReleaseAction"],
        1,
        false
      );
    } else {
      if (!this.hasSendedClickCommand) {
        const canExecuteCommand: boolean =
          await this.handleOpenPopupRequestConfirmCommandExecution();

        if (canExecuteCommand) {
          this.SendCommand(this.ComponentAPI.properties["ClickAction"]);
          this.navigateToSupervision();
          this.hasSendedClickCommand = true;
        }
      }
    }
  }
}
